/**
 * AudioWaveform — EAA-1755.
 *
 * Seek surface for AudioPlayer. When the Web Audio API is available and the
 * source can be decoded, it renders a clickable SVG waveform (played portion
 * tinted). Otherwise it transparently falls back to a plain range slider (the
 * "line"), so callers always get a working seek control regardless of support
 * or CORS. Responsive: the SVG scales to its container width.
 */

import * as React from 'react';
import { cn } from '../../lib/utils';

export interface AudioWaveformProps {
    src: string;
    duration: number;
    current: number;
    onSeek: (time: number) => void;
    /** Number of waveform bars. */
    bars?: number;
    className?: string;
}

type AudioContextCtor = typeof AudioContext;

function getAudioContextCtor(): AudioContextCtor | null {
    if (typeof window === 'undefined') return null;
    const w = window as unknown as {
        AudioContext?: AudioContextCtor;
        webkitAudioContext?: AudioContextCtor;
    };
    return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export const AudioWaveform: React.FC<AudioWaveformProps> = ({
    src,
    duration,
    current,
    onSeek,
    bars = 56,
    className,
}) => {
    const [peaks, setPeaks] = React.useState<number[] | null>(null);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    // Decode the source into normalised peaks; any failure → line fallback.
    React.useEffect(() => {
        const Ctor = getAudioContextCtor();
        if (!Ctor || !src) {
            setPeaks(null);
            return undefined;
        }
        let cancelled = false;
        setPeaks(null);
        void (async () => {
            try {
                const res = await fetch(src);
                const raw = await res.arrayBuffer();
                const ctx = new Ctor();
                const audioBuffer = await ctx.decodeAudioData(raw);
                void ctx.close();
                if (cancelled) return;
                const data = audioBuffer.getChannelData(0);
                const block = Math.floor(data.length / bars) || 1;
                const result: number[] = [];
                let max = 0;
                for (let i = 0; i < bars; i += 1) {
                    let peak = 0;
                    for (let j = 0; j < block; j += 1) {
                        const v = Math.abs(data[i * block + j] || 0);
                        if (v > peak) peak = v;
                    }
                    result.push(peak);
                    if (peak > max) max = peak;
                }
                setPeaks(max > 0 ? result.map((v) => v / max) : result);
            } catch {
                if (!cancelled) setPeaks(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [src, bars]);

    const progress = duration > 0 ? current / duration : 0;

    const handleClickSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        const el = containerRef.current;
        if (!el || duration <= 0) return;
        const rect = el.getBoundingClientRect();
        const fraction = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        onSeek(fraction * duration);
    };

    // Fallback: plain range "line".
    if (!peaks) {
        const pct = progress * 100;
        return (
            <input
                type="range"
                min={0}
                max={duration || 0}
                step={0.01}
                value={current}
                onChange={(e) => onSeek(Number(e.target.value))}
                aria-label="Seek"
                className={cn(// h-6 + py-2.5 + bg-clip-content: 24px touch height, but the painted track stays 4px thin.
                    'audio-waveform-line h-6 cursor-pointer bg-clip-content py-2.5 accent-primary', className)}
                style={{
                    background: `linear-gradient(to right, hsl(var(--primary)) ${pct}%, hsl(var(--border)) ${pct}%)`,
                }}
            />
        );
    }

    return (
        <div
            ref={containerRef}
            onClick={handleClickSeek}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(current)}
            className={cn('audio-waveform flex min-h-11 items-center cursor-pointer', className)}
        >
            <svg
                viewBox={`0 0 ${peaks.length} 40`}
                preserveAspectRatio="none"
                className="audio-waveform-svg block h-8 w-full"
            >
                {peaks.map((p, i) => {
                    const h = Math.max(2, p * 40);
                    const y = (40 - h) / 2;
                    const played = (i + 0.5) / peaks.length <= progress;
                    return (
                        <rect
                            key={i}
                            x={i + 0.15}
                            y={y}
                            width={0.7}
                            height={h}
                            rx={0.3}
                            className={played ? 'fill-[hsl(var(--primary))]' : 'fill-[hsl(var(--border))]'}
                        />
                    );
                })}
            </svg>
        </div>
    );
};

export default AudioWaveform;
